"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { type MindstringPrototype } from "@/generated/fastapi";
import {
  useConfirmThoughtsMutation,
  useThoughtRefresh,
} from "@/features/thoughts";
import { useUserChoices } from "@/state/globalStore";
import type { ThoughtReassignData } from "@/state/globalStoreSlices/userChoices";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import {
  DndContext,
  closestCenter,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import useSortableStudioThoughts from "../../hooks/useSortableStudioThoughts";
import type { SelectedThoughtDocument } from "../../types";
import { getSameListReorderResult } from "./arrangementReorder";
import {
  getSituationIdFromStudioDropId,
  isStudioSituationDropId,
} from "../../utils/situationDropTargets";
import { syncThoughtOrderIfChanged } from "../../utils";

type StudioArrangementRegistration = {
  prototype: MindstringPrototype;
  setHasManuallyReordered: Dispatch<SetStateAction<boolean>>;
  setSortedThoughts: Dispatch<SetStateAction<SelectedThoughtDocument[]>>;
  situationId: string;
  sortedThoughts: SelectedThoughtDocument[];
};

type StudioArrangementDndContextValue = {
  dragState: StudioArrangementDragState;
  registerArrangement: (
    registration: StudioArrangementRegistration,
  ) => () => void;
};

type StudioArrangementDragState = {
  activeSituationId: string | null;
  targetSituationId: string | null;
};

type StudioArrangementDndProviderProps = {
  children: ReactNode;
  isMobileDevice: boolean;
};

const EMPTY_DRAG_STATE: StudioArrangementDragState = {
  activeSituationId: null,
  targetSituationId: null,
};

const StudioArrangementDndContext =
  createContext<StudioArrangementDndContextValue | null>(null);

const arrangementCollisionDetection: CollisionDetection = (args) => {
  const collisions = closestCenter(args);
  const closestCollision = collisions[0];

  if (!closestCollision || isStudioSituationDropId(closestCollision.id)) {
    return collisions;
  }

  return collisions.filter(({ id }) => !isStudioSituationDropId(id));
};

function getThoughtId(id: UniqueIdentifier) {
  return String(id);
}

function getTargetThoughtOrder({
  activeThought,
  overId,
  targetThoughts,
  targetSituationId,
}: {
  activeThought: SelectedThoughtDocument;
  overId: UniqueIdentifier | null | undefined;
  targetThoughts: SelectedThoughtDocument[];
  targetSituationId: string;
}) {
  const targetThoughtId = overId ? getThoughtId(overId) : null;
  const targetThoughtsWithoutActive = targetThoughts.filter(
    (thought) => thought.objectID !== activeThought.objectID,
  );
  const movedThought: SelectedThoughtDocument = {
    ...activeThought,
    situation_id: targetSituationId,
  };

  if (!targetThoughtId || isStudioSituationDropId(overId)) {
    return [...targetThoughtsWithoutActive, movedThought];
  }

  const insertIndex = targetThoughtsWithoutActive.findIndex(
    (thought) => thought.objectID === targetThoughtId,
  );

  if (insertIndex < 0) {
    return [...targetThoughtsWithoutActive, movedThought];
  }

  return [
    ...targetThoughtsWithoutActive.slice(0, insertIndex),
    movedThought,
    ...targetThoughtsWithoutActive.slice(insertIndex),
  ];
}

export function useStudioArrangementDndRegistration(
  registration: StudioArrangementRegistration,
) {
  const context = useContext(StudioArrangementDndContext);

  useEffect(() => {
    if (!context) {
      throw new Error(
        "useStudioArrangementDndRegistration must be used within StudioArrangementDndProvider",
      );
    }

    return context.registerArrangement(registration);
  }, [context, registration]);
}

export function useStudioArrangementDropState(situationId: string) {
  const context = useContext(StudioArrangementDndContext);

  if (!context) {
    throw new Error(
      "useStudioArrangementDropState must be used within StudioArrangementDndProvider",
    );
  }

  return {
    isDragging: Boolean(context.dragState.activeSituationId),
    isSource: context.dragState.activeSituationId === situationId,
    isTarget: context.dragState.targetSituationId === situationId,
  };
}

export default function StudioArrangementDndProvider({
  children,
  isMobileDevice,
}: StudioArrangementDndProviderProps) {
  const arrangementsRef = useRef(
    new Map<string, StudioArrangementRegistration>(),
  );
  const [dragState, setDragState] =
    useState<StudioArrangementDragState>(EMPTY_DRAG_STATE);
  const { reassignSelectedThoughts, reorderSituationThoughtSelection } =
    useUserChoices();
  const confirmThoughtsMutation = useConfirmThoughtsMutation();
  const { refreshReflectionSurfaces, syncConfirmedThoughtMutation } =
    useThoughtRefresh();
  const { setIsDragging, sensors } = useSortableStudioThoughts({
    isMobileDevice,
  });

  const registerArrangement = useCallback(
    (registration: StudioArrangementRegistration) => {
      arrangementsRef.current.set(registration.situationId, registration);

      return () => {
        const currentRegistration = arrangementsRef.current.get(
          registration.situationId,
        );

        if (currentRegistration === registration) {
          arrangementsRef.current.delete(registration.situationId);
        }
      };
    },
    [],
  );

  const findArrangementByThoughtId = useCallback((thoughtId: string) => {
    for (const arrangement of arrangementsRef.current.values()) {
      const thought = arrangement.sortedThoughts.find(
        (item) => item.objectID === thoughtId,
      );

      if (thought) {
        return {
          arrangement,
          thought,
        };
      }
    }

    return null;
  }, []);

  const getSituationIdForCollisionId = useCallback(
    (id: UniqueIdentifier | null | undefined) => {
      const situationDropId = getSituationIdFromStudioDropId(id);

      if (situationDropId || !id) {
        return situationDropId;
      }

      return (
        findArrangementByThoughtId(getThoughtId(id))?.arrangement.situationId ??
        null
      );
    },
    [findArrangementByThoughtId],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const sourceSituationId =
        findArrangementByThoughtId(getThoughtId(event.active.id))?.arrangement
          .situationId ?? null;

      setIsDragging(true);
      setDragState({
        activeSituationId: sourceSituationId,
        targetSituationId: sourceSituationId,
      });
    },
    [findArrangementByThoughtId, setIsDragging],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const targetSituationId = getSituationIdForCollisionId(event.over?.id);

      setDragState((currentState) => ({
        ...currentState,
        targetSituationId,
      }));
    },
    [getSituationIdForCollisionId],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      const activeThoughtId = getThoughtId(active.id);
      const sourceMatch = findArrangementByThoughtId(activeThoughtId);

      if (!sourceMatch || !over) {
        return;
      }

      const targetSituationId = getSituationIdForCollisionId(over.id);

      if (!targetSituationId) {
        return;
      }

      const sourceArrangement = sourceMatch.arrangement;

      if (targetSituationId === sourceArrangement.situationId) {
        const reorderResult = getSameListReorderResult(
          sourceArrangement.sortedThoughts,
          active.id,
          over.id,
        );

        if (!reorderResult) {
          return;
        }

        sourceArrangement.setSortedThoughts(reorderResult.reorderedThoughts);
        sourceArrangement.setHasManuallyReordered(true);

        syncThoughtOrderIfChanged(
          sourceArrangement.sortedThoughts.map((thought) => thought.objectID),
          reorderResult.orderedThoughtIds,
          (orderedThoughtIds) =>
            reorderSituationThoughtSelection({
              prototypeCategory: sourceArrangement.prototype,
              situationId: sourceArrangement.situationId,
              orderedThoughtIds,
            }),
        );
        return;
      }

      const targetArrangement = arrangementsRef.current.get(targetSituationId);

      if (!targetArrangement) {
        return;
      }

      const movedItem: ThoughtReassignData = {
        objectID: sourceMatch.thought.objectID,
        prototype: sourceArrangement.prototype,
        situation_id:
          sourceMatch.thought.situation_id ?? sourceArrangement.situationId,
      };
      const nextTargetThoughts = getTargetThoughtOrder({
        activeThought: sourceMatch.thought,
        overId: over.id,
        targetThoughts: targetArrangement.sortedThoughts,
        targetSituationId,
      });
      const nextTargetThoughtIds = nextTargetThoughts.map(
        (thought) => thought.objectID,
      );

      try {
        const response = await confirmThoughtsMutation.mutateAsync({
          assign_situation_id: targetSituationId,
          reset_reflections: true,
          thought_ids: [activeThoughtId],
        });

        if (response.status !== "success" && response.status !== "pending") {
          showErrorToast(response.message || "Failed to anchor thought");
          return;
        }

        if (response.status === "pending") {
          showWarningToast(
            response.message ||
              "Thought anchored, but search is still catching up.",
          );
        } else {
          showSuccessToast("Thought anchored", 1800);
        }
      } catch {
        showErrorToast("Failed to anchor thought");
        return;
      }

      reassignSelectedThoughts([movedItem], targetSituationId);
      reorderSituationThoughtSelection({
        prototypeCategory: targetArrangement.prototype,
        situationId: targetSituationId,
        orderedThoughtIds: nextTargetThoughtIds,
      });

      sourceArrangement.setSortedThoughts((thoughts) =>
        thoughts.filter((thought) => thought.objectID !== activeThoughtId),
      );
      targetArrangement.setSortedThoughts(nextTargetThoughts);
      sourceArrangement.setHasManuallyReordered(true);
      targetArrangement.setHasManuallyReordered(true);
      syncConfirmedThoughtMutation([sourceArrangement.prototype]);
      refreshReflectionSurfaces();
    },
    [
      confirmThoughtsMutation,
      findArrangementByThoughtId,
      getSituationIdForCollisionId,
      refreshReflectionSurfaces,
      reassignSelectedThoughts,
      reorderSituationThoughtSelection,
      syncConfirmedThoughtMutation,
    ],
  );

  const contextValue = useMemo(
    () => ({ dragState, registerArrangement }),
    [dragState, registerArrangement],
  );

  return (
    <StudioArrangementDndContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={arrangementCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={(event) => {
          setIsDragging(false);
          handleDragEnd(event);
          setDragState(EMPTY_DRAG_STATE);
        }}
        onDragCancel={() => {
          setIsDragging(false);
          setDragState(EMPTY_DRAG_STATE);
        }}
        modifiers={[restrictToVerticalAxis]}
      >
        {children}
      </DndContext>
    </StudioArrangementDndContext.Provider>
  );
}
